import uniqueByKey from 'common/util/uniqueByKey';

import type {
  BaseAction,
  BulkLoadedAction,
  BulkRemoveAction,
  ErrorAction,
  LoadedAction,
  QueryAction,
} from 'common/actions/queryActionFactory';

export type QueryState<D, QP> = {
  cursor?: string | null;
  error: string | null;
  hasNextPage?: boolean;
  items?: D[] | null;
  lastUpdated: number | null;
  loading: boolean;
  loadingMore: boolean;
  queryKey: string;
  queryParams: QP;
};

export type QueriesState<D, QP> = {
  [queryKey: string]: QueryState<D, QP>;
} & {
  needsInvalidate?: boolean;
};

export const defaultState: QueriesState<any, any> = {};

type QueryObject = {
  [key: string]: unknown;
};
const queryReducerFactory = <D extends QueryObject, QP extends Record<string, unknown>>({
  bulkLoadedType,
  bulkRemoveType,
  errorType,
  invalidateType,
  loadedMoreType,
  loadedType,
  requestMoreType,
  requestType,
  uniqueKey = '_id',
}: {
  bulkLoadedType: string;
  bulkRemoveType: string;
  errorType: string;
  invalidateType: string;
  loadedMoreType: string;
  loadedType: string;
  requestMoreType: string;
  requestType: string;
  uniqueKey?: keyof D;
}) => {
  function getUpdatedState(
    state: QueriesState<D, QP>,
    queryKey: string,
    update: Partial<QueryState<D, QP>>
  ) {
    const prevQueryState = state[queryKey];
    const newQueryState = Object.assign({}, prevQueryState, update);
    return Object.assign({}, state, {
      [queryKey]: newQueryState,
    });
  }

  return function reducer(
    state: QueriesState<D, QP> = defaultState,
    action: QueryAction<D, QP>
  ): QueriesState<D, QP> {
    switch (action.type) {
      case requestType: {
        const requestAction = action as BaseAction;
        return getUpdatedState(state, requestAction.queryKey, {
          error: null,
          lastUpdated: requestAction.timestamp,
          loading: true,
          loadingMore: false,
          queryKey: requestAction.queryKey,
        });
      }

      case bulkRemoveType: {
        const bulkRemoveAction = action as BulkRemoveAction<D, QP>;

        const itemsToRemove = bulkRemoveAction.queries.flatMap((query) => query.items);
        const itemsToRemoveKeys = new Set(itemsToRemove.map((item) => item[uniqueKey]));

        return Object.keys(state).reduce((acc, queryKey) => {
          const existingItems = state[queryKey]?.items ?? [];
          const filteredItems = existingItems.filter((item) => {
            const itemKey = item[uniqueKey];
            return !itemsToRemoveKeys.has(itemKey);
          });

          if (bulkRemoveAction.deleteQueryIfEmpty && filteredItems.length === 0) {
            const newState = { ...acc };
            delete newState[queryKey];
            return newState;
          }

          if (filteredItems.length !== existingItems.length) {
            return getUpdatedState(acc, queryKey, {
              items: filteredItems,
              lastUpdated: bulkRemoveAction.timestamp,
            });
          }

          return acc;
        }, state);
      }

      case bulkLoadedType: {
        const bulkLoadedAction = action as BulkLoadedAction<D, QP>;
        return bulkLoadedAction.queries.reduce((acc, query) => {
          return getUpdatedState(acc, query.queryKey, {
            cursor: query.queryResponse.cursor,
            error: null,
            hasNextPage: query.queryResponse.hasNextPage,
            items: query.queryResponse.items,
            lastUpdated: bulkLoadedAction.timestamp,
            loading: false,
            loadingMore: false,
            queryParams: query.queryParams,
          });
        }, state);
      }

      case loadedType: {
        const loadedAction = action as LoadedAction<D, QP>;
        return getUpdatedState(state, loadedAction.queryKey, {
          cursor: loadedAction.cursor,
          error: null,
          hasNextPage: loadedAction.hasNextPage,
          items: loadedAction.items,
          lastUpdated: loadedAction.timestamp,
          loading: false,
          loadingMore: false,
          queryParams: loadedAction.queryParams,
        });
      }

      case requestMoreType: {
        const requestMoreAction = action as BaseAction;
        return getUpdatedState(state, requestMoreAction.queryKey, {
          error: null,
          lastUpdated: requestMoreAction.timestamp,
          loading: false,
          loadingMore: true,
        });
      }

      case loadedMoreType: {
        const loadedAction = action as LoadedAction<D, QP>;
        const existingItems = state[loadedAction.queryKey]?.items ?? [];
        const newItems = loadedAction.items;
        return getUpdatedState(state, loadedAction.queryKey, {
          cursor: loadedAction.cursor,
          error: null,
          hasNextPage: loadedAction.hasNextPage,
          items: uniqueByKey([...existingItems, ...newItems], uniqueKey),
          lastUpdated: loadedAction.timestamp,
          loading: false,
          loadingMore: false,
        });
      }

      case errorType: {
        const errorAction = action as ErrorAction;
        return getUpdatedState(state, errorAction.queryKey, {
          error: errorAction.error,
          lastUpdated: errorAction.timestamp,
          loading: false,
          loadingMore: false,
        });
      }

      case invalidateType: {
        return Object.assign({}, state, {
          needsInvalidate: true,
        });
      }

      default:
        return state;
    }
  };
};

export default queryReducerFactory;
